import React, { useState } from 'react';
import { X, KeyRound, Loader2, AlertCircle, CheckCircle2, Eye, EyeOff } from 'lucide-react';
import { apiService } from '../services/api';
import { useTheme } from '../contexts/ThemeContext';

interface ChangePasswordProps {
  onClose: () => void;
}

const ChangePassword: React.FC<ChangePasswordProps> = ({ onClose }) => {
  const { theme } = useTheme();
  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (error) setError(null);
    if (success) setSuccess(false);
  };

  const togglePasswordVisibility = (field: 'current' | 'new' | 'confirm') => {
    setShowPasswords((prev) => ({ ...prev, [field]: !prev[field] }));
  };

  const validateForm = (): string | null => {
    if (!formData.currentPassword) {
      return 'Ingresa tu contraseña actual';
    }
    if (!formData.newPassword) {
      return 'Ingresa una nueva contraseña';
    }
    if (formData.newPassword.length < 8) {
      return 'La nueva contraseña debe tener al menos 8 caracteres';
    }
    if (formData.newPassword === formData.currentPassword) {
      return 'La nueva contraseña debe ser diferente a la actual';
    }
    if (formData.newPassword !== formData.confirmPassword) {
      return 'Las contraseñas no coinciden';
    }
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    setIsLoading(true);
    setError(null);
    setSuccess(false);

    try {
      const response = await apiService.changePassword(
        formData.currentPassword,
        formData.newPassword
      );

      if (response.success) {
        setSuccess(true);
        setFormData({
          currentPassword: '',
          newPassword: '',
          confirmPassword: '',
        });

        // Cerrar modal después de 2 segundos
        setTimeout(() => {
          onClose();
        }, 2000);
      } else {
        setError(response.error || 'Error al cambiar contraseña');
      }
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Error inesperado');
    } finally {
      setIsLoading(false);
    }
  };

  const getPasswordStrength = (
    password: string
  ): { strength: string; color: string; width: string } => {
    if (password.length === 0) return { strength: '', color: '', width: '0%' };
    if (password.length < 8)
      return { strength: 'Débil', color: `${theme.colors.action.danger}`, width: '33%' };

    let strength = 0;
    if (password.length >= 12) strength++;
    if (/[a-z]/.test(password) && /[A-Z]/.test(password)) strength++;
    if (/\d/.test(password)) strength++;
    if (/[^a-zA-Z0-9]/.test(password)) strength++;

    if (strength <= 1)
      return { strength: 'Media', color: `${theme.colors.bg.tertiary}`, width: '66%' };
    return { strength: 'Fuerte', color: `${theme.colors.bg.tertiary}`, width: '100%' };
  };

  const passwordStrength = getPasswordStrength(formData.newPassword);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div
        className={`${theme.colors.bg.input} dark:${theme.colors.bg.tertiary} rounded-2xl shadow-xl max-w-md w-full`}
      >
        {/* Header */}
        <div
          className={`flex items-center justify-between p-6 border-b ${theme.colors.border.primary} dark:${theme.colors.border.primary}`}
        >
          <h2
            className={`text-xl font-semibold ${theme.colors.text.primary} dark:${theme.colors.text.inverse} flex items-center`}
          >
            <KeyRound className="w-5 h-5 mr-2" />
            Cambiar Contraseña
          </h2>
          <button
            onClick={onClose}
            className={`p-2 hover:${theme.colors.bg.secondary} dark:hover:${theme.colors.bg.tertiary} rounded-lg transition-colors`}
          >
            <X className={`w-5 h-5 ${theme.colors.text.secondary}`} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Success Message */}
          {success && (
            <div
              className={`p-3 ${theme.colors.bg.secondary} dark:bg-green-900/20 border ${theme.colors.border.primary} dark:border-green-800 rounded-lg flex items-center`}
            >
              <CheckCircle2 className="w-4 h-4 text-nb-success mr-2 flex-shrink-0" />
              <span className={`text-sm ${theme.colors.text.primary} dark:text-nb-success`}>
                Contraseña actualizada correctamente
              </span>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div
              className={`p-3 ${theme.colors.bg.secondary} dark:bg-red-900/20 border ${theme.colors.border.primary} dark:${theme.colors.border.primary} rounded-lg flex items-center`}
            >
              <AlertCircle className={`w-4 h-4 ${theme.colors.action.danger} mr-2 flex-shrink-0`} />
              <span
                className={`text-sm ${theme.colors.action.danger} dark:${theme.colors.action.danger}`}
              >
                {error}
              </span>
            </div>
          )}

          {/* Current Password */}
          <div>
            <label
              htmlFor="current-password"
              className={`block text-sm font-medium ${theme.colors.text.primary} dark:${theme.colors.text.primary} mb-2`}
            >
              Contraseña Actual
            </label>
            <div className="relative">
              <input
                type={showPasswords.current ? 'text' : 'password'}
                id="current-password"
                name="currentPassword"
                value={formData.currentPassword}
                onChange={handleInputChange}
                className={`w-full px-4 py-3 pr-12 ${theme.colors.bg.input} dark:${theme.colors.bg.tertiary} border ${theme.colors.border.primary} dark:${theme.colors.border.primary} rounded-lg focus:outline-none focus:ring-2 focus:ring-nb-accent focus:border-transparent transition-all`}
                placeholder="Tu contraseña actual"
                disabled={isLoading}
              />
              <button
                type="button"
                onClick={() => togglePasswordVisibility('current')}
                className={`absolute right-3 top-1/2 -translate-y-1/2 ${theme.colors.text.secondary} hover:${theme.colors.text.primary} dark:hover:${theme.colors.text.primary}`}
              >
                {showPasswords.current ? (
                  <EyeOff className="w-5 h-5" />
                ) : (
                  <Eye className="w-5 h-5" />
                )}
              </button>
            </div>
          </div>

          {/* New Password */}
          <div>
            <label
              htmlFor="new-password"
              className={`block text-sm font-medium ${theme.colors.text.primary} dark:${theme.colors.text.primary} mb-2`}
            >
              Nueva Contraseña
            </label>
            <div className="relative">
              <input
                type={showPasswords.new ? 'text' : 'password'}
                id="new-password"
                name="newPassword"
                value={formData.newPassword}
                onChange={handleInputChange}
                className={`w-full px-4 py-3 pr-12 ${theme.colors.bg.input} dark:${theme.colors.bg.tertiary} border ${theme.colors.border.primary} dark:${theme.colors.border.primary} rounded-lg focus:outline-none focus:ring-2 focus:ring-nb-accent focus:border-transparent transition-all`}
                placeholder="Mínimo 8 caracteres"
                disabled={isLoading}
              />
              <button
                type="button"
                onClick={() => togglePasswordVisibility('new')}
                className={`absolute right-3 top-1/2 -translate-y-1/2 ${theme.colors.text.secondary} hover:${theme.colors.text.primary} dark:hover:${theme.colors.text.primary}`}
              >
                {showPasswords.new ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>

            {/* Password Strength Indicator */}
            {formData.newPassword && (
              <div className="mt-2">
                <div className="flex items-center justify-between mb-1">
                  <span
                    className={`text-xs ${theme.colors.text.secondary} dark:${theme.colors.text.secondary}`}
                  >
                    Seguridad: <span className="font-medium">{passwordStrength.strength}</span>
                  </span>
                </div>
                <div
                  className={`h-2 ${theme.colors.bg.secondary} dark:${theme.colors.bg.tertiary} rounded-full overflow-hidden`}
                >
                  <div
                    className={`h-full ${passwordStrength.color} transition-all duration-300`}
                    style={{ width: passwordStrength.width }}
                  />
                </div>
              </div>
            )}

            <p
              className={`text-xs ${theme.colors.text.secondary} dark:${theme.colors.text.secondary} mt-2`}
            >
              Usa al menos 8 caracteres con mayúsculas, minúsculas y números
            </p>
          </div>

          {/* Confirm Password */}
          <div>
            <label
              htmlFor="confirm-password"
              className={`block text-sm font-medium ${theme.colors.text.primary} dark:${theme.colors.text.primary} mb-2`}
            >
              Confirmar Nueva Contraseña
            </label>
            <div className="relative">
              <input
                type={showPasswords.confirm ? 'text' : 'password'}
                id="confirm-password"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleInputChange}
                className={`w-full px-4 py-3 pr-12 ${theme.colors.bg.input} dark:${theme.colors.bg.tertiary} border ${theme.colors.border.primary} dark:${theme.colors.border.primary} rounded-lg focus:outline-none focus:ring-2 focus:ring-nb-accent focus:border-transparent transition-all`}
                placeholder="Repite tu nueva contraseña"
                disabled={isLoading}
              />
              <button
                type="button"
                onClick={() => togglePasswordVisibility('confirm')}
                className={`absolute right-3 top-1/2 -translate-y-1/2 ${theme.colors.text.secondary} hover:${theme.colors.text.primary} dark:hover:${theme.colors.text.primary}`}
              >
                {showPasswords.confirm ? (
                  <EyeOff className="w-5 h-5" />
                ) : (
                  <Eye className="w-5 h-5" />
                )}
              </button>
            </div>
          </div>

          {/* Actions */}
          <div className="flex space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              disabled={isLoading}
              className={`flex-1 px-4 py-3 border ${theme.colors.border.primary} dark:${theme.colors.border.primary} ${theme.colors.text.primary} dark:${theme.colors.text.primary} rounded-lg hover:${theme.colors.bg.primary} dark:hover:${theme.colors.bg.tertiary} transition-colors disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className={`flex-1 bg-nb-accent hover:bg-nb-accent/90 ${theme.colors.text.inverse} font-medium py-3 px-4 rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center`}
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  Actualizando...
                </>
              ) : (
                <>
                  <KeyRound className="w-4 h-4 mr-2" />
                  Cambiar Contraseña
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ChangePassword;
